import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router';
import type { z } from 'zod';
import {
  toWorkspaceItemsSearchParams,
  workspaceItemsQueryShape,
} from '@bower/shared/schemas/workspaceItems.schema';
import type { WorkspaceItemsQuery } from '@bower/shared';
import { useAuth } from '../../contexts/AuthContext';
import { useWorkspaceId } from '../../contexts/WorkspaceContext';
import {
  useWorkspaceViewPrefsStore,
  workspaceViewPrefsKey,
  type WorkspaceView,
} from '../../stores/workspaceViewPrefsStore';
import { logger } from '../../services/logging.service';
import { DESTINATION_VIEW_STATE, isDestination } from '../../utils/workspaceNavState';

/** `folders` is not in the URL: the layout decides it (`folderListParams`). */
export interface WorkspaceViewParams extends Omit<
  WorkspaceItemsQuery,
  'cursor' | 'limit' | 'parent' | 'folders'
> {
  /** `'folders'` is the icon layout the product calls "Folder view". */
  view: WorkspaceView;
}

export interface UseWorkspaceViewParams {
  readonly params: WorkspaceViewParams;
  /**
   * Replaces the history entry for filter edits (kinds, createdBy, tags,
   * modified range) so typing into a filter does not fill the back stack;
   * pushes one for view, scope and sort/order, which are navigations a user
   * expects Back to undo.
   */
  readonly set: (patch: Partial<WorkspaceViewParams>) => void;
  readonly clearFilters: () => void;
  /** Active filter dimensions: kinds, createdBy, tags, modified range (0 to 4). */
  readonly activeFilterCount: number;
}

type QueryParams = Omit<WorkspaceViewParams, 'view'>;

const DEFAULT_VIEW: WorkspaceView = 'folders';
const VIEWS: readonly WorkspaceView[] = ['folders', 'cards', 'list'];
const FILTER_KEYS = ['kinds', 'createdBy', 'tags', 'modifiedFrom', 'modifiedTo'] as const;
const PUSH_KEYS: ReadonlyArray<keyof WorkspaceViewParams> = ['view', 'scope', 'sort', 'order'];
const QUERY_KEYS = ['scope', 'sort', 'order', ...FILTER_KEYS] as const;
/** The workspace-wide choice: everything but scope, which belongs to a location. */
const CHOICE_KEYS = ['view', 'sort', 'order', ...FILTER_KEYS] as const;
/** Every default, including `parent`/`limit`, which the serializer then omits. */
const QUERY_DEFAULTS: WorkspaceItemsQuery = workspaceItemsQueryShape.parse({});

const shape = workspaceItemsQueryShape.shape;

/** One field against its own schema: invalid input falls back to that field's default. */
export function field<S extends z.ZodType>(
  schema: S,
  search: URLSearchParams,
  key: string,
): z.output<S> {
  // A repeated list param (`?tags=a&tags=b`) reaches the schema as an array.
  const all = search.getAll(key);
  const result = schema.safeParse(all.length > 1 ? all : all[0]);
  return result.success ? result.data : schema.parse(undefined);
}

/** Never throws: a hand-edited or stale URL degrades field by field. */
function parseQuery(search: URLSearchParams): QueryParams {
  const query: QueryParams = {
    scope: field(shape.scope, search, 'scope'),
    kinds: field(shape.kinds, search, 'kinds'),
    createdBy: field(shape.createdBy, search, 'createdBy'),
    tags: field(shape.tags, search, 'tags'),
    modifiedFrom: field(shape.modifiedFrom, search, 'modifiedFrom'),
    modifiedTo: field(shape.modifiedTo, search, 'modifiedTo'),
    sort: field(shape.sort, search, 'sort'),
    order: field(shape.order, search, 'order'),
  };
  // The route rejects from > to; an inverted range is one bad field, so it
  // falls back to no range rather than a request that 400s.
  if (
    query.modifiedFrom &&
    query.modifiedTo &&
    Date.parse(query.modifiedFrom) > Date.parse(query.modifiedTo)
  ) {
    return { ...query, modifiedFrom: undefined, modifiedTo: undefined };
  }
  return query;
}

function parseView(search: URLSearchParams): WorkspaceView | undefined {
  const raw = search.get('view');
  return VIEWS.find((v) => v === raw);
}

/**
 * A comma is the list separator, so a tag containing one cannot be written to
 * the URL (the shared serializer throws). Drop it, keep the rest, and say so.
 */
function serializableTags(tags: string[] | undefined): string[] | undefined {
  if (!tags) return tags;
  const kept = tags.filter((t) => !t.includes(','));
  if (kept.length !== tags.length) {
    logger.warn('Workspace view: dropped tag filter values containing a comma', {
      component: 'useWorkspaceViewParams',
      metadata: { droppedCount: tags.length - kept.length },
    });
  }
  return kept.length > 0 ? kept : undefined;
}

/** Writes `query` over `prev`, leaving every param this hook does not own intact. */
function writeQuery(prev: URLSearchParams, query: QueryParams): URLSearchParams {
  const out = new URLSearchParams(prev);
  for (const key of QUERY_KEYS) out.delete(key);
  const serialized = toWorkspaceItemsSearchParams({ ...QUERY_DEFAULTS, ...query });
  for (const [key, value] of serialized) out.set(key, value);
  return out;
}

/**
 * Writes the whole of `params` over `prev`. The view is always written, even
 * the default, so the entry names its choice: Back to it restores that choice
 * instead of falling through to whatever is remembered now.
 */
function writeParams(prev: URLSearchParams, params: WorkspaceViewParams): URLSearchParams {
  const { view, ...query } = params;
  const out = writeQuery(prev, { ...query, tags: serializableTags(query.tags) });
  out.set('view', view);
  return out;
}

const namesChoice = (search: URLSearchParams): boolean =>
  CHOICE_KEYS.some((key) => search.has(key));

/**
 * The effective params: the URL's scope, plus the choice from the URL when it
 * names any part of it (then it is the whole choice and its unnamed fields
 * take their defaults, so a shared link reproduces its view), else the
 * remembered choice.
 */
export function effectiveParams(
  search: URLSearchParams,
  remembered: string | undefined,
): WorkspaceViewParams {
  const source = namesChoice(search)
    ? search
    : new URLSearchParams(typeof remembered === 'string' ? remembered : '');
  return {
    ...parseQuery(source),
    scope: field(shape.scope, search, 'scope'),
    view: parseView(source) ?? DEFAULT_VIEW,
  };
}

/** This user's remembered choice for the current workspace, as stored. */
function useRememberedChoice(): { key: string; remembered: string | undefined } {
  const { user } = useAuth();
  const workspaceId = useWorkspaceId();
  const key = workspaceViewPrefsKey(user?.uid ?? 'anonymous', workspaceId ?? 'none');
  const remembered = useWorkspaceViewPrefsStore((s) => s.choices[key]);
  return { key, remembered };
}

/**
 * The params a collection opened at its bare path will read: the remembered
 * choice, default scope. `usePrefetchFolder` warms exactly this key.
 */
export function useBarePathViewParams(): WorkspaceViewParams {
  const { remembered } = useRememberedChoice();
  return useMemo(() => effectiveParams(new URLSearchParams(), remembered), [remembered]);
}

/** The choice half of `params` as the remembered query string (no scope). */
function choiceString(params: WorkspaceViewParams): string {
  const choice = writeParams(new URLSearchParams(), params);
  choice.delete('scope');
  return choice.toString();
}

/**
 * The workspace page's view, sort and filter state, held in the URL so a link
 * reproduces the view and Back undoes a layout or sort change. The query half
 * is parsed and serialized with the shared `GET /api/workspace/items`
 * contract, so the URL and the request cannot drift apart.
 *
 * View, sort and filters are ONE workspace-wide choice, remembered per user
 * per workspace (`workspaceViewPrefsStore`), so moving between collections
 * keeps them. Every link opens a collection at its bare path; a URL that
 * names none of the choice reads the remembered one, and this hook writes it
 * into the URL by replacing the entry (no new history), so the URL stays the
 * source of truth on the page. A URL that names any of it (a shared link, a
 * Back) wins whole and becomes the remembered choice, unless it opened a
 * destination page (`DESTINATION_VIEW_STATE`). Scope belongs to each
 * location: a bare path is "This collection".
 *
 * Several components call this for one page. Each derives the same params,
 * and the effect is idempotent: the fill replaces with the same URL, and an
 * unchanged choice is not stored again.
 *
 * `_folderId` is unused (the choice is workspace-wide); it is kept so the
 * call sites, which all have the folder, need not change.
 */
export function useWorkspaceViewParams(_folderId: string | null): UseWorkspaceViewParams {
  const [search, setSearch] = useSearchParams();
  const destination = isDestination(useLocation().state);
  const { key: prefKey, remembered } = useRememberedChoice();
  const setChoice = useWorkspaceViewPrefsStore((s) => s.setChoice);

  const params = useMemo(() => effectiveParams(search, remembered), [search, remembered]);
  const urlNamesChoice = namesChoice(search);
  const choice = choiceString(params);

  useEffect(() => {
    if (!urlNamesChoice) {
      setSearch((prev) => writeParams(prev, params), { replace: true });
    } else if (!destination && choice !== remembered) {
      setChoice(prefKey, choice);
    }
  }, [urlNamesChoice, destination, choice, remembered, params, prefKey, setChoice, setSearch]);

  /**
   * Patches the effective params (not the URL's alone, which may name nothing
   * yet). On a destination page only a Type filter edit (the filter the page
   * owns; "Show everything" included) makes the choice the user's; any other
   * change keeps the destination state, or the page's own filter would be
   * remembered with it.
   */
  const update = useCallback(
    (patch: Partial<WorkspaceViewParams>, replace: boolean) => {
      const keepDestination = destination && !('kinds' in patch);
      setSearch(
        (prev) => {
          const stored = useWorkspaceViewPrefsStore.getState().choices[prefKey];
          return writeParams(prev, { ...effectiveParams(prev, stored), ...patch });
        },
        { replace, state: keepDestination ? DESTINATION_VIEW_STATE : undefined },
      );
    },
    [setSearch, prefKey, destination],
  );

  const set = useCallback(
    (patch: Partial<WorkspaceViewParams>) =>
      update(patch, !PUSH_KEYS.some((key) => patch[key] !== undefined)),
    [update],
  );

  const clearFilters = useCallback(
    () =>
      update(
        {
          kinds: undefined,
          createdBy: undefined,
          tags: undefined,
          modifiedFrom: undefined,
          modifiedTo: undefined,
        },
        true,
      ),
    [update],
  );

  const activeFilterCount =
    Number(Boolean(params.kinds?.length)) +
    Number(Boolean(params.createdBy?.length)) +
    Number(Boolean(params.tags?.length)) +
    Number(Boolean(params.modifiedFrom || params.modifiedTo));

  return { params, set, clearFilters, activeFilterCount };
}
